import { Platform } from 'react-native';

// Conditionally import AsyncStorage only for native platforms
let AsyncStorage: any = null;
if (Platform.OS !== 'web') {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
}

/**
 * Unified storage service that works on both native and web
 * Uses AsyncStorage for native and localStorage for web
 */
class StorageService {
  private isWeb = Platform.OS === 'web';

  async setItem(key: string, value: string): Promise<void> {
    try {
      if (this.isWeb) {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem(key, value);
        } else {
          console.warn('localStorage not available, data will not persist');
        }
      } else {
        if (AsyncStorage) {
          await AsyncStorage.setItem(key, value);
        } else {
          throw new Error('AsyncStorage not available');
        }
      }
    } catch (error) {
      console.error('Storage setItem error:', error);
      throw new Error(`Failed to store data for key: ${key}`);
    }
  }

  async getItem(key: string): Promise<string | null> {
    try {
      if (this.isWeb) {
        if (typeof window !== 'undefined' && window.localStorage) {
          return localStorage.getItem(key);
        } else {
          console.warn('localStorage not available');
          return null;
        }
      } else {
        if (AsyncStorage) {
          return await AsyncStorage.getItem(key);
        } else {
          console.error('AsyncStorage not available');
          return null;
        }
      }
    } catch (error) {
      console.error('Storage getItem error:', error);
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      if (this.isWeb) {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.removeItem(key);
        }
      } else {
        if (AsyncStorage) {
          await AsyncStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Storage removeItem error:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      if (this.isWeb) {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.clear();
        }
      } else {
        if (AsyncStorage) {
          await AsyncStorage.clear();
        }
      }
    } catch (error) {
      console.error('Storage clear error:', error);
    }
  }

  // Helper methods for JSON data
  async setJSON<T>(key: string, value: T): Promise<void> {
    await this.setItem(key, JSON.stringify(value));
  }

  async getJSON<T>(key: string): Promise<T | null> {
    const item = await this.getItem(key);
    if (!item) return null;
    
    try {
      return JSON.parse(item) as T;
    } catch (error) {
      console.error('JSON parse error:', error);
      return null;
    }
  }
}

export const storage = new StorageService();